import { useTranslation } from 'react-i18next';
import SelectMenu from '../../forms/SelectMenu';
import { Controller, useForm } from 'react-hook-form';
import TextInput from '../../forms/TextInput';
import { useMemo } from 'react';
import { useAppSelector } from '../../../hooks/useAppSelector';
import * as accountingSlice from '../../../data/accounting.slice';

const heightSelections = [...new Array(37)].map((_, i) => {
  const feet = Math.floor((i + 48) / 12);
  const inches = (i + 48) % 12;
  return {
    label: `${feet}' ${inches}"`,
    value: `${i + 48}`,
  };
});

interface ConfirmationDialogProps {
  form: any; // Parent form instance
  onFieldChange: (fieldName: string, value: any) => void;
  index: number;
}

export default function GurneyForm({
  form,
  onFieldChange,
  index,
}: ConfirmationDialogProps) {
  const { t } = useTranslation('transport');
  const { control, formState } = form; // Use parent form's control

  const getError = (fieldName: string) => {
    if (formState.errors.MultiLegTrips) {
      const trips = formState.errors.MultiLegTrips as any;
      if (trips.length > index) {
        const trip = trips[index];
        return trip?.[fieldName]?.message
          ? `${trip[fieldName].message}`
          : undefined;
      }
    }
    return undefined;
  };

  const masterAttributeList = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );

  const oxygenAttributes = useMemo(
    () =>
      masterAttributeList
        .filter(
          (attr) => attr.transportationAttributeCategoryBC === 'OXYGEN_LITERS',
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [masterAttributeList],
  );
  const stairsAttributes = useMemo(
    () =>
      masterAttributeList
        .filter(
          (attr) => attr.transportationAttributeCategoryBC === 'STAIRS_COUNT',
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [masterAttributeList],
  );

  const oxygenSelections = useMemo(
    () =>
      oxygenAttributes
        .map((attr) => ({
          label: attr.name,
          value: attr.transportationAttributeUUID,
        }))
        .sort((a, b) => {
          const aNum = parseInt(a.label);
          const bNum = parseInt(b.label);
          return aNum - bNum;
        }),
    [oxygenAttributes],
  );

  const stairsSelections = useMemo(
    () => [
      {
        label: t('reservation.stairsNone'),
        value: ``,
      },
      ...stairsAttributes
        .map((attr) => ({
          label: attr.name,
          value: attr.transportationAttributeUUID,
        }))
        .sort((a, b) => {
          const aNum = parseInt(a.label);
          const bNum = parseInt(b.label);
          return aNum - bNum;
        }),
    ],
    [stairsAttributes],
  );

  return (
    <>
      <SelectMenu
        label={t('reservation.memberHeight')}
        name={`MultiLegTrips[${index}].height`}
        values={heightSelections}
        valueAttr="value"
        labelAttr="label"
        required={true}
        control={control}
        errorMessage={getError(`MultiLegTrips[${index}].height`)}
        rules={{
          required: t('reservation.requiredField'),
        }}
        onChange={(e) => {
          onFieldChange(`MultiLegTrips[${index}].height`, e.target.value);
        }}
      />
      <TextInput
        name={`MultiLegTrips[${index}].weight`}
        label={t('reservation.memberWeight')}
        control={control}
        required={true}
        errors={formState.errors}
        type="number"
        rules={{
          required: t('reservation.requiredField'),
          validate: (value) => {
            if (!value || value === '') return t('reservation.requiredField');
            const num = parseInt(value);
            if (isNaN(num) || num < 1) return 'Weight must be positive';
            if (num > 999) return t('reservation.weightValidation');
            return true;
          },
        }}
        inputProps={{
          min: 1,
          max: 999,
        }}
        InputProps={{
          type: 'number',
        }}
        onChange={(e) => {
          onFieldChange(`MultiLegTrips[${index}].weight`, e.target.value);
        }}
      />
      <SelectMenu
        label={t('reservation.isOxygenRequired')}
        name={`MultiLegTrips[${index}].AttributeLevel4`}
        values={oxygenSelections}
        valueAttr="value"
        labelAttr="label"
        required={false}
        control={control}
        onChange={(e) => {
          onFieldChange(
            `MultiLegTrips[${index}].AttributeLevel4`,
            e.target.value,
          );
        }}
      />
      <SelectMenu
        label={t('reservation.numberOfStairs')}
        name={`MultiLegTrips[${index}].AttributeLevel5`}
        values={stairsSelections}
        valueAttr="value"
        labelAttr="label"
        required={false}
        control={control}
        onChange={(e) => {
          onFieldChange(
            `MultiLegTrips[${index}].AttributeLevel5`,
            e.target.value,
          );
        }}
      />
    </>
  );
}
